/**
 * SessionDetailView — full-width detail page for Engine Sessions,
 * shown in the main content area when the session indicator in the StatusBar is clicked.
 *
 * Multi-level navigation:
 *   Level 1: Session list overview
 *   Level 2: Session detail (stats, definitions, execution order, open-file button)
 */

import type { ExecutionUnitInfo, ExecutionUnitOutput } from "@magic-md/core";
import type {
    SessionSummary,
} from "@magic-md/engine-client/protocol";
import { useT } from "@magic-md/i18n/react";
import {
    ArrowLeft,
    ChevronRight,
    ExternalLink,
    FileText,
    Layers,
    RefreshCw,
    Repeat,
    Users,
    Zap,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { cn } from "../lib/utils";
import { getPlatformService } from "../platform";
import { useEditorStore } from "../store/editor-store";
import { useEngineOverviewStore } from "../store/engine-overview-store";
import { usePluginExecutionStore } from "../store/plugin-execution-store";
import { getThemeAccentStyle } from "../theme/runtime/theme-accents";

/* ── Types ── */

interface SessionStateSnapshot {
    sessionId: string;
    units: ExecutionUnitInfo[];
    executionOrder: string[];
    outputs: Record<string, ExecutionUnitOutput>;
}

/* ── Main Component ── */

export function SessionDetailView() {
    const t = useT("editor");
    const setShowSessionDetail = useEditorStore(
        (s) => s.setShowSessionDetail,
    );
    const openFileFromPath = useEditorStore((s) => s.openFileFromPath);
    const activeSessionId = usePluginExecutionStore((s) => s.sessionId);

    const [drillSessionId, setDrillSessionId] = useState<string | null>(null);
    const [sessionState, setSessionState] =
        useState<SessionStateSnapshot | null>(null);
    const sessions = useEngineOverviewStore((s) => s.sessions);
    const mcpClients = useEngineOverviewStore((s) => s.mcpClients);
    const loading = useEngineOverviewStore(
        (s) => s.loadingScopes.sessions || s.loadingScopes.mcp,
    );
    const error = useEngineOverviewStore((s) => s.error);
    const retainOverviewScopes = useEngineOverviewStore((s) => s.retainScopes);
    const refreshNow = useEngineOverviewStore((s) => s.refreshNow);

    /* Fetch session detail when drilled in */
    const fetchSessionDetail = useCallback(async (sid: string) => {
        try {
            const engine = getPlatformService().engine;
            const state = await engine.sessionGetState({ sessionId: sid });
            setSessionState(state);
        } catch {
            setSessionState(null);
        }
    }, []);

    useEffect(() => {
        return retainOverviewScopes(["sessions", "mcp"]);
    }, [retainOverviewScopes]);

    /* Fetch session state when drilled in */
    useEffect(() => {
        if (drillSessionId) {
            fetchSessionDetail(drillSessionId);
        } else {
            setSessionState(null);
        }
    }, [drillSessionId, fetchSessionDetail]);

    const backToList = () => {
        setDrillSessionId(null);
        setSessionState(null);
    };

    /* Breadcrumb */
    const drilledSession = drillSessionId
        ? sessions.find((s) => s.sessionId === drillSessionId)
        : null;
    const drilledFileName = drilledSession?.filePath
        ? (drilledSession.filePath.split("/").pop() ??
            drilledSession.filePath.split("\\").pop() ??
            drilledSession.filePath)
        : drillSessionId;

    return (
        <div className="flex flex-col h-full bg-background">
            {/* Top bar */}
            <div className="flex items-center gap-2 h-10 px-4 border-b border-border shrink-0 bg-muted/20">
                <button
                    type="button"
                    className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                    onClick={() => {
                        if (drillSessionId) {
                            backToList();
                        } else {
                            setShowSessionDetail(false);
                        }
                    }}
                >
                    <ArrowLeft size={14} />
                    <span>{t("common.back")}</span>
                </button>
                <div className="w-px h-4 bg-border" />

                {/* Breadcrumb */}
                {drillSessionId ? (
                    <div className="flex items-center gap-1.5 text-xs">
                        <button
                            type="button"
                            className="text-muted-foreground hover:text-foreground transition-colors"
                            onClick={backToList}
                        >
                            {t("sessionDetail.sessions")}
                        </button>
                        <ChevronRight
                            size={12}
                            className="text-muted-foreground/40"
                        />
                        <span className="text-foreground font-medium flex items-center gap-1">
                            <FileText size={12} />
                            {drilledFileName}
                        </span>
                    </div>
                ) : (
                    <span className="text-xs text-muted-foreground">
                        {t("sessionDetail.sessions")}
                    </span>
                )}

                <div className="flex-1" />
                <button
                    type="button"
                    onClick={() => {
                        void refreshNow(["sessions", "mcp"]);
                        if (drillSessionId) fetchSessionDetail(drillSessionId);
                    }}
                    disabled={loading}
                    className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
                >
                    <RefreshCw
                        size={12}
                        className={loading ? "animate-spin" : ""}
                    />
                    <span>{t("common.refresh")}</span>
                </button>
            </div>

            {/* Content */}
            <div className="flex-1 min-h-0 overflow-auto">
                <div className="max-w-3xl mx-auto px-6 py-8 space-y-6">
                    {/* Error */}
                    {error ? (
                        <div className="flex items-center gap-2 rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3">
                            <span className="text-sm text-destructive">
                                {error}
                            </span>
                        </div>
                    ) : null}

                    {drillSessionId ? (
                        /* Level 2: Session Detail */
                        <SessionDetail
                            session={drilledSession ?? null}
                            sessionId={drillSessionId}
                            isActive={drillSessionId === activeSessionId}
                            sessionState={sessionState}
                            onOpenFile={openFileFromPath}
                        />
                    ) : (
                        /* Level 1: Overview */
                        <>
                            {/* Header */}
                            <div className="flex items-start gap-4">
                                <div
                                    className="theme-accent-subtle flex h-14 w-14 items-center justify-center rounded-xl border shrink-0"
                                    style={getThemeAccentStyle("success")}
                                >
                                    <Layers
                                        size={24}
                                        className="theme-accent-text"
                                        style={getThemeAccentStyle("success")}
                                    />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <h1 className="text-xl font-bold text-foreground tracking-tight">
                                        {t("sessionDetail.sessions")}
                                    </h1>
                                    <p className="text-sm text-muted-foreground mt-1">
                                        {t("sessionDetail.headerDescription")}
                                    </p>
                                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                                        <Badge>
                                            {t("sessionDetail.sessionCount", { count: sessions.length })}
                                        </Badge>
                                        {mcpClients.length > 0 && (
                                            <Badge>
                                                {t("sessionDetail.mcpClientCount", { count: mcpClients.length })}
                                            </Badge>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Session List */}
                            <Section
                                title={t("sessionDetail.activeSessions")}
                                icon={<Layers size={14} />}
                                count={sessions.length}
                            >
                                {sessions.length === 0 ? (
                                    <div className="text-xs text-muted-foreground/60 py-2">
                                        {t("sessionDetail.noSessions")}
                                    </div>
                                ) : (
                                    <div className="space-y-1">
                                        {sessions.map((s) => {
                                            const isActive =
                                                s.sessionId === activeSessionId;
                                            const fileName = s.filePath
                                                ? (s.filePath
                                                    .split("/")
                                                    .pop() ??
                                                    s.filePath
                                                        .split("\\")
                                                        .pop() ??
                                                    s.filePath)
                                                : s.sessionId;

                                            return (
                                                <button
                                                    key={s.sessionId}
                                                    type="button"
                                                    className="flex items-center gap-2 w-full rounded-lg border border-border/30 px-3 py-2.5 text-left hover:bg-muted/30 transition-colors group"
                                                    onClick={() =>
                                                        setDrillSessionId(
                                                            s.sessionId,
                                                        )
                                                    }
                                                >
                                                    <span
                                                        className={cn(
                                                            "w-2 h-2 rounded-full shrink-0",
                                                            isActive
                                                                ? "theme-accent-fill"
                                                                : "bg-muted-foreground/20",
                                                        )}
                                                        style={
                                                            isActive
                                                                ? getThemeAccentStyle("success")
                                                                : undefined
                                                        }
                                                    />
                                                    <FileText
                                                        size={14}
                                                        className="text-muted-foreground/50 shrink-0"
                                                    />
                                                    <div className="flex-1 min-w-0">
                                                        <div className="text-xs font-medium text-foreground/80 truncate">
                                                            {fileName}
                                                        </div>
                                                        {s.filePath ? (
                                                            <div className="text-[10px] text-muted-foreground/40 truncate">
                                                                {s.filePath}
                                                            </div>
                                                        ) : null}
                                                    </div>
                                                    <div className="flex items-center gap-2 shrink-0">
                                                        <span className="text-[10px] text-muted-foreground/50">
                                                            {s.unitCount}u · {s.outputCount}o
                                                        </span>
                                                        {isActive ? (
                                                            <span
                                                                className="theme-accent-chip rounded px-1 py-0.5 text-[8px] font-semibold uppercase"
                                                                style={getThemeAccentStyle("success")}
                                                            >
                                                                {t("common.active")}
                                                            </span>
                                                        ) : null}
                                                        <ChevronRight
                                                            size={12}
                                                            className="text-muted-foreground/30 group-hover:text-muted-foreground/60 transition-colors"
                                                        />
                                                    </div>
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}
                            </Section>

                            {/* MCP Connected Clients */}
                            {mcpClients.length > 0 && (
                                <Section
                                    title={t("sessionDetail.connectedClients")}
                                    icon={<Users size={14} />}
                                    count={mcpClients.length}
                                >
                                    <div className="space-y-1">
                                        {mcpClients.map((c) => (
                                            <div
                                                key={c.sessionId}
                                                className="flex items-center gap-2 rounded-lg border border-border/30 px-3 py-2.5"
                                            >
                                                <span
                                                    className="theme-accent-fill h-2 w-2 rounded-full shrink-0"
                                                    style={getThemeAccentStyle("success")}
                                                />
                                                <Users
                                                    size={14}
                                                    className="text-muted-foreground/50 shrink-0"
                                                />
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-xs font-medium text-foreground/80">
                                                        {c.clientName ??
                                                            t("mcpDetail.clients.unknown")}
                                                        {c.clientVersion
                                                            ? ` v${c.clientVersion}`
                                                            : ""}
                                                    </div>
                                                    <div className="text-[10px] text-muted-foreground/40">
                                                        {t("sessionDetail.connectedAt", {
                                                            time: formatTimestamp(c.connectedAt, t),
                                                        })}
                                                    </div>
                                                </div>
                                                <code className="text-[9px] font-mono text-muted-foreground/40 shrink-0">
                                                    {c.sessionId.slice(0, 8)}
                                                </code>
                                            </div>
                                        ))}
                                    </div>
                                </Section>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

/* ── Session Detail (Level 2) ── */

function SessionDetail({
    session,
    sessionId,
    isActive,
    sessionState,
    onOpenFile,
}: {
    session: SessionSummary | null;
    sessionId: string;
    isActive: boolean;
    sessionState: SessionStateSnapshot | null;
    onOpenFile: (filePath: string) => Promise<void>;
}) {
    const t = useT("editor");
    const filePath = session?.filePath ?? null;
    const fileName = filePath
        ? (filePath.split("/").pop() ??
            filePath.split("\\").pop() ??
            filePath)
        : sessionId;

    return (
        <div className="space-y-6">
            {/* Session header */}
            <div className="flex items-start gap-4">
                <div
                    className={cn(
                        "w-14 h-14 rounded-xl border flex items-center justify-center shrink-0",
                        isActive
                            ? "theme-accent-subtle"
                            : "border-border bg-muted/30",
                    )}
                    style={isActive ? getThemeAccentStyle("success") : undefined}
                >
                    <FileText
                        size={24}
                        className={
                            isActive
                                ? "theme-accent-text"
                                : "text-muted-foreground/40"
                        }
                        style={isActive ? getThemeAccentStyle("success") : undefined}
                    />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                        <h1 className="text-xl font-bold text-foreground tracking-tight truncate">
                            {fileName}
                        </h1>
                        {isActive ? (
                            <span
                                className="theme-accent-chip shrink-0 rounded-md px-1.5 py-0.5 text-[10px] font-semibold uppercase"
                                style={getThemeAccentStyle("success")}
                            >
                                {t("common.active")}
                            </span>
                        ) : null}
                    </div>
                    {filePath ? (
                        <p className="text-xs text-muted-foreground/60 mt-0.5 truncate">
                            {filePath}
                        </p>
                    ) : null}
                    <div className="flex items-center gap-2 mt-2">
                        <Badge>{t("sessionDetail.sessionBadge")}</Badge>
                        <code className="text-[10px] font-mono text-muted-foreground/50 break-all">
                            {sessionId}
                        </code>
                    </div>
                </div>
            </div>

            {/* Open file button */}
            {filePath ? (
                <button
                    type="button"
                    className="flex items-center gap-2 rounded-lg border border-primary/30 bg-primary/5 px-4 py-2.5 text-sm font-medium text-primary hover:bg-primary/10 transition-colors w-full justify-center"
                    onClick={() => onOpenFile(filePath)}
                >
                    <ExternalLink size={14} />
                    {t("sessionDetail.openFile")}
                </button>
            ) : null}

            {/* Session state detail */}
            {sessionState ? (() => {
                const definitions = sessionState.units.filter((u): u is import("@magic-md/core").DefinitionUnitInfo => u.unitType === "definition");
                const forDefinitions = sessionState.units.filter((u): u is import("@magic-md/core").ForUnitInfo => u.unitType === "for");
                return (
                    <>
                        {/* Stats grid */}
                        <div className="grid grid-cols-3 gap-2">
                            <InfoCard label={t("sessionDetail.definitions")}>
                                <span className="text-lg font-bold text-foreground">
                                    {definitions.length}
                                </span>
                            </InfoCard>
                            <InfoCard label={t("sessionDetail.outputs")}>
                                <span className="text-lg font-bold text-foreground">
                                    {Object.keys(sessionState.outputs).length}
                                </span>
                            </InfoCard>
                            <InfoCard label={t("sessionDetail.executionOrder")}>
                                <span className="text-lg font-bold text-foreground">
                                    {sessionState.executionOrder.length}
                                </span>
                            </InfoCard>
                        </div>

                        {/* For-block stats (only if present) */}
                        {forDefinitions.length > 0 ? (
                            <div className="grid grid-cols-3 gap-2">
                                <InfoCard label={t("sessionDetail.forBlocks")}>
                                    <span className="text-lg font-bold text-foreground">
                                        {forDefinitions.length}
                                    </span>
                                </InfoCard>
                                <InfoCard label={t("sessionDetail.forOutputs")}>
                                    <span className="text-lg font-bold text-foreground">
                                        {Object.keys(sessionState.outputs).filter(k => {
                                            const o = sessionState.outputs[k];
                                            return o?.unitType === "for";
                                        }).length}
                                    </span>
                                </InfoCard>
                                <InfoCard label={t("sessionDetail.totalIterations")}>
                                    <span className="text-lg font-bold text-foreground">
                                        {forDefinitions.reduce(
                                            (sum, f) => sum + (f.iterationCount ?? 0),
                                            0,
                                        )}
                                    </span>
                                </InfoCard>
                            </div>
                        ) : null}

                        {/* Definitions */}
                        {definitions.length > 0 ? (
                            <Section
                                title={t("sessionDetail.definitions")}
                                icon={<Layers size={14} />}
                                count={definitions.length}
                            >
                                <div className="space-y-1">
                                    {definitions.map((def) => (
                                        <div
                                            key={def.id}
                                            className="flex items-center gap-2 rounded-lg border border-border/30 px-3 py-2"
                                        >
                                            <span
                                                className={cn(
                                                    "w-2 h-2 rounded-full shrink-0",
                                                    def.hasOutput
                                                        ? "theme-accent-fill"
                                                        : "bg-muted-foreground/20",
                                                )}
                                                style={
                                                    def.hasOutput
                                                        ? getThemeAccentStyle("success")
                                                        : undefined
                                                }
                                            />
                                            <code className="text-[11px] font-mono text-foreground/80 truncate flex-1">
                                                {def.id}
                                            </code>
                                            <Badge>{def.pluginName}</Badge>
                                            {def.hasOutput ? (
                                                <span
                                                    className="theme-accent-text text-[9px] font-medium"
                                                    style={getThemeAccentStyle("success")}
                                                >
                                                    {t("pluginDetail.hasOutput")}
                                                </span>
                                            ) : null}
                                        </div>
                                    ))}
                                </div>
                            </Section>
                        ) : null}

                        {/* For Definitions */}
                        {forDefinitions.length > 0 ? (
                            <Section
                                title={t("sessionDetail.forBlocks")}
                                icon={<Repeat size={14} />}
                                count={forDefinitions.length}
                            >
                                <div className="space-y-1">
                                    {forDefinitions.map((forDef) => (
                                        <div
                                            key={forDef.id}
                                            className="rounded-lg border border-border/30 px-3 py-2 space-y-1"
                                        >
                                            <div className="flex items-center gap-2">
                                                <span
                                                    className={cn(
                                                        "w-2 h-2 rounded-full shrink-0",
                                                        forDef.hasOutput
                                                            ? "theme-accent-fill"
                                                            : "bg-muted-foreground/20",
                                                    )}
                                                    style={
                                                        forDef.hasOutput
                                                            ? getThemeAccentStyle("info")
                                                            : undefined
                                                    }
                                                />
                                                <code className="text-[11px] font-mono text-foreground/80 truncate flex-1">
                                                    ::for[{forDef.each}] of {forDef.of}
                                                </code>
                                                <Badge>{forDef.id}</Badge>
                                                {forDef.hasOutput ? (
                                                    <span
                                                        className="theme-accent-text text-[9px] font-medium"
                                                        style={getThemeAccentStyle("info")}
                                                    >
                                                        {t("sessionDetail.iterations", { count: forDef.iterationCount ?? 0 })}
                                                    </span>
                                                ) : null}
                                            </div>
                                            <div className="flex items-center gap-2 pl-4 text-[10px] text-muted-foreground/60">
                                                <span>
                                                    {t("sessionDetail.innerDefinitions", { count: forDef.innerDefinitionCount })}
                                                </span>
                                                {forDef.innerPluginNames.length > 0 ? (
                                                    <span className="text-muted-foreground/40">
                                                        ({forDef.innerPluginNames.join(", ")})
                                                    </span>
                                                ) : null}
                                                {forDef.limit != null ? (
                                                    <span>limit={forDef.limit}</span>
                                                ) : null}
                                                {forDef.concurrency != null ? (
                                                    <span>concurrency={forDef.concurrency}</span>
                                                ) : null}
                                                {forDef.meta ? (
                                                    <span
                                                        className="theme-accent-text"
                                                        style={getThemeAccentStyle(
                                                            forDef.meta.failed > 0
                                                                ? "error"
                                                                : "success",
                                                        )}
                                                    >
                                                        {forDef.meta.succeeded}/{forDef.meta.total} ok · {forDef.meta.duration}ms
                                                    </span>
                                                ) : null}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </Section>
                        ) : null}

                        {/* Execution Order */}
                        {sessionState.executionOrder.length > 0 ? (
                            <Section
                                title={t("sessionDetail.executionOrder")}
                                icon={<Zap size={14} />}
                            >
                                <div className="flex items-center gap-1 flex-wrap">
                                    {sessionState.executionOrder.map((id, i) => {
                                        const def = sessionState.units.find(
                                            (d) => d.id === id,
                                        );
                                        return (
                                            <span
                                                key={id}
                                                className="flex items-center gap-1"
                                            >
                                                {i > 0 ? (
                                                    <ChevronRight
                                                        size={10}
                                                        className="text-muted-foreground/30"
                                                    />
                                                ) : null}
                                                <code
                                                    className={cn(
                                                        "text-[10px] font-mono px-1.5 py-0.5 rounded border",
                                                        def?.hasOutput
                                                            ? "theme-accent-chip"
                                                            : "border-border/30 bg-muted/20 text-foreground/70",
                                                    )}
                                                    style={
                                                        def?.hasOutput
                                                            ? getThemeAccentStyle("success")
                                                            : undefined
                                                    }
                                                >
                                                    {id}
                                                </code>
                                            </span>
                                        );
                                    })}
                                </div>
                            </Section>
                        ) : null}
                    </>
                );
            })() : (
                <div className="text-xs text-muted-foreground/60 py-4 text-center">
                    {t("sessionDetail.loadingState")}
                </div>
            )}
        </div>
    );
}

/* ── Helper Components ── */

function Section({
    title,
    icon,
    count,
    children,
}: {
    title: string;
    icon?: React.ReactNode;
    count?: number;
    children: React.ReactNode;
}) {
    return (
        <div className="rounded-xl border border-border/50 bg-card/50 overflow-hidden">
            <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border/50 bg-muted/20">
                {icon ? (
                    <span className="text-muted-foreground">{icon}</span>
                ) : null}
                <h3 className="text-xs font-semibold text-foreground uppercase tracking-wider">
                    {title}
                </h3>
                {count !== undefined ? (
                    <span className="text-[10px] text-muted-foreground/60 bg-muted/50 rounded-full px-1.5 py-0.5">
                        {count}
                    </span>
                ) : null}
            </div>
            <div className="px-4 py-3">{children}</div>
        </div>
    );
}

function Badge({ children }: { children: React.ReactNode }) {
    return (
        <span className="inline-block rounded-md border border-border/50 bg-muted/30 text-muted-foreground px-1.5 py-0.5 text-[10px]">
            {children}
        </span>
    );
}

function InfoCard({
    label,
    children,
}: {
    label: string;
    children: React.ReactNode;
}) {
    return (
        <div className="rounded-lg border border-border/30 bg-muted/10 px-3 py-2">
            <div className="text-[9px] text-muted-foreground/40 uppercase tracking-wider font-semibold mb-1">
                {label}
            </div>
            {children}
        </div>
    );
}

/* ── Utilities ── */

function formatTimestamp(
    epoch: number,
    t?: ReturnType<typeof useT>,
): string {
    const now = Date.now();
    const diff = now - epoch;
    if (diff < 60_000) return t?.("sessionDetail.time.justNow") ?? "just now";
    if (diff < 3_600_000) {
        const count = Math.floor(diff / 60_000);
        return t?.("sessionDetail.time.minutesAgo", { count }) ?? `${count}m ago`;
    }
    if (diff < 86_400_000) {
        const count = Math.floor(diff / 3_600_000);
        return t?.("sessionDetail.time.hoursAgo", { count }) ?? `${count}h ago`;
    }
    return new Date(epoch).toLocaleDateString();
}
